/**
 * Pure rules for the Portal dashboard's "needs attention" module.
 *
 * The module reads the compact installation summary the Portal already
 * holds (heartbeat, maintenance and attention rows — never audit content)
 * and turns it into an honest headline: loading, unavailable, nothing
 * registered, all clear, or a ranked list of things to look at.
 */

export interface InstallationsSummaryLike {
  totalActive: number;
  totalStale: number;
  needsUpdate: number;
  needsAttention: number;
  maintenanceRunning: number;
}

export interface InstallationsPayloadLike {
  summary?: Partial<InstallationsSummaryLike> | null;
  degraded?: boolean;
}

export type AttentionSeverity = 'danger' | 'warning' | 'info';

export interface AttentionItem {
  key: 'attention' | 'stale' | 'outdated' | 'maintenance';
  severity: AttentionSeverity;
  count: number;
  label: string;
}

export type InstallationAttentionState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'forbidden' }
  | { kind: 'none-registered' }
  | { kind: 'clear'; activeCount: number }
  | { kind: 'items'; activeCount: number; severity: AttentionSeverity; headline: string; items: AttentionItem[] };

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

const coerceSummary = (summary?: Partial<InstallationsSummaryLike> | null): InstallationsSummaryLike => ({
  totalActive: Number(summary?.totalActive ?? 0) || 0,
  totalStale: Number(summary?.totalStale ?? 0) || 0,
  needsUpdate: Number(summary?.needsUpdate ?? 0) || 0,
  needsAttention: Number(summary?.needsAttention ?? 0) || 0,
  maintenanceRunning: Number(summary?.maintenanceRunning ?? 0) || 0
});

/**
 * Derives the module state from an /api/instances response. A degraded
 * payload (the inventory store was unreachable) is reported as unavailable
 * rather than as "all clear": zero counts from a dead store are not evidence.
 */
export const buildInstallationAttention = (
  payload: InstallationsPayloadLike | undefined,
  httpStatus: number
): InstallationAttentionState => {
  if (httpStatus === 401 || httpStatus === 403) return { kind: 'forbidden' };
  if (httpStatus < 200 || httpStatus >= 300 || !payload || payload.degraded) return { kind: 'unavailable' };

  const summary = coerceSummary(payload.summary);
  const activeCount = summary.totalActive + summary.totalStale;
  const items: AttentionItem[] = [];
  if (summary.needsAttention > 0) {
    items.push({ key: 'attention', severity: 'warning', count: summary.needsAttention, label: `${plural(summary.needsAttention, 'installation needs', 'installations need')} attention` });
  }
  if (summary.totalStale > 0) {
    items.push({ key: 'stale', severity: 'warning', count: summary.totalStale, label: `${plural(summary.totalStale, 'installation has', 'installations have')} a stale heartbeat` });
  }
  if (summary.needsUpdate > 0) {
    items.push({ key: 'outdated', severity: 'info', count: summary.needsUpdate, label: `${plural(summary.needsUpdate, 'installation is', 'installations are')} on an older web part version` });
  }
  if (summary.maintenanceRunning > 0) {
    items.push({ key: 'maintenance', severity: 'info', count: summary.maintenanceRunning, label: `${plural(summary.maintenanceRunning, 'maintenance run is', 'maintenance runs are')} in flight` });
  }

  if (items.length === 0) {
    return activeCount === 0 ? { kind: 'none-registered' } : { kind: 'clear', activeCount };
  }

  const severity: AttentionSeverity = items.some(item => item.severity === 'warning') ? 'warning' : 'info';
  return { kind: 'items', activeCount, severity, headline: items[0].label, items };
};
